import { useRef, type ReactNode } from 'react';
import {
  Dialog,
  HStack,
  Portal,
  Text,
  VStack,
  useBreakpointValue,
  useSlotRecipe,
  type Dialog as ChakraDialog,
} from '@chakra-ui/react';
import { RiftboundButton } from './button';
import { useKeyboardBottomInset } from '../../hooks/useKeyboardBottomInset';
import { dialogSlotRecipe } from '../../theme/recipes/dialog';

export type RiftboundAlertDialogConfirmTreatment = 'primary' | 'destructive';

export interface RiftboundAlertDialogProps {
  open: boolean;
  title: ReactNode;
  /** Primary message shown below the title. */
  description?: ReactNode;
  /** Extra body content after the description (lists, fields, etc.). */
  children?: ReactNode;
  cancelLabel?: string;
  /** When false, the cancel control is omitted. Backdrop and escape still call `onClose`. */
  showCancel?: boolean;
  /** Cancel button action. Defaults to `onClose`. */
  onCancel?: () => void;
  confirmLabel?: string;
  /** Optional middle action (e.g. “Leave without saving”). */
  secondaryLabel?: string;
  /** Visual weight of the middle action. Defaults to ghost so it stays quieter than confirm. */
  secondaryTreatment?: 'secondary' | 'ghost';
  /** `destructive` uses a red ghost confirm button (delete, restart, …). */
  confirmTreatment?: RiftboundAlertDialogConfirmTreatment;
  loading?: boolean;
  error?: ReactNode;
  /** Shows “This cannot be undone.” below the description. */
  irreversible?: boolean;
  size?: ChakraDialog.RootProps['size'];
  maxW?: string;
  /** When false, the first field in the body can take focus instead of Cancel. */
  autoFocusCancel?: boolean;
  /** Overrides the default cancel-button focus target. */
  initialFocusEl?: () => HTMLElement | null;
  closeOnInteractOutside?: boolean;
  /** Backdrop stacking order. The panel sits one step above this. */
  zIndex?: number;
  /** Center the title and description, and stack the actions full width. */
  centered?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onSecondary?: () => void;
}

/**
 * Confirmation alert.
 * Mobile matches an Apple alert: a centered card, inset from the screen, with
 * the cancel action leading and the preferred action trailing.
 * https://developer.apple.com/design/human-interface-guidelines/alerts
 * Desktop stays a centered dialog with a compact button footer.
 */
export function RiftboundAlertDialog({
  open,
  title,
  description,
  children,
  cancelLabel = 'Cancel',
  showCancel = true,
  onCancel,
  confirmLabel = 'Confirm',
  secondaryLabel,
  secondaryTreatment = 'ghost',
  confirmTreatment = 'destructive',
  loading = false,
  error,
  irreversible = false,
  size = 'sm',
  maxW,
  autoFocusCancel = true,
  initialFocusEl,
  closeOnInteractOutside = false,
  zIndex = 1800,
  centered = false,
  onClose,
  onConfirm,
  onSecondary,
}: RiftboundAlertDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRecipe = useSlotRecipe({ recipe: dialogSlotRecipe });
  const dialogChrome = dialogRecipe({}).content;
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const keyboardInset = useKeyboardBottomInset(open && isMobile);
  const stackedActions = Boolean(secondaryLabel && onSecondary && showCancel);

  return (
    <Dialog.Root
      open={open}
      role="alertdialog"
      size={size}
      placement="center"
      motionPreset="scale"
      closeOnInteractOutside={closeOnInteractOutside}
      initialFocusEl={initialFocusEl ?? (autoFocusCancel ? () => cancelRef.current : undefined)}
      onOpenChange={(event) => {
        if (!event.open) onClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop bg="blackAlpha.700" style={{ backdropFilter: 'blur(4px)' }} zIndex={zIndex} />
        <Dialog.Positioner zIndex={zIndex + 1}>
          <Dialog.Content
            css={dialogChrome}
            borderWidth={isMobile ? 0 : 'hairline'}
            mx={isMobile ? 6 : 4}
            maxW={isMobile ? '20rem' : maxW}
            w={isMobile ? 'full' : undefined}
            borderRadius={isMobile ? '2xl' : undefined}
            style={
              isMobile && keyboardInset > 0
                ? { marginBottom: keyboardInset }
                : undefined
            }
          >
            <Dialog.Header textAlign={centered ? 'center' : undefined} px={isMobile ? 5 : undefined} pt={isMobile ? 5 : undefined} pb={isMobile ? 0 : undefined}>
              <Dialog.Title
                fontSize={isMobile ? 'lg' : 'xl'}
                fontWeight="semibold"
                fontFamily="heading"
                w={centered ? 'full' : undefined}
                textAlign={centered ? 'center' : undefined}
              >
                {title}
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body px={isMobile ? 5 : undefined} pt={isMobile ? 2 : undefined} pb={isMobile ? 4 : undefined}>
              <VStack align={centered ? 'center' : 'stretch'} gap={2}>
                {description ? (
                  typeof description === 'string' ? (
                    <Dialog.Description asChild>
                      <Text fontSize="sm" color="textMuted" textAlign={centered ? 'center' : undefined}>
                        {description}
                      </Text>
                    </Dialog.Description>
                  ) : (
                    description
                  )
                ) : null}
                {children}
                {irreversible ? (
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="textMuted"
                    textAlign={centered ? 'center' : undefined}
                  >
                    This cannot be undone.
                  </Text>
                ) : null}
                {error ? (
                  <Text fontSize="sm" color="danger" textAlign={centered ? 'center' : undefined}>
                    {error}
                  </Text>
                ) : null}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer px={isMobile ? 4 : undefined} pt={isMobile ? 0 : undefined} pb={isMobile ? 4 : undefined}>
              {isMobile ? (
                <VStack gap={2} align="stretch" w="full">
                  {stackedActions && secondaryLabel && onSecondary ? (
                    <RiftboundButton
                      treatment="secondary"
                      onClick={onSecondary}
                      disabled={loading}
                      w="full"
                      minH="44px"
                      borderRadius="full"
                      bg="whiteAlpha.200"
                      borderColor="transparent"
                    >
                      {secondaryLabel}
                    </RiftboundButton>
                  ) : null}
                  <HStack gap={2} w="full">
                    {showCancel ? (
                      <RiftboundButton
                        ref={cancelRef}
                        treatment="secondary"
                        onClick={onCancel ?? onClose}
                        disabled={loading}
                        flex="1"
                        minH="44px"
                        borderRadius="full"
                        bg="whiteAlpha.200"
                        borderColor="transparent"
                      >
                        {cancelLabel}
                      </RiftboundButton>
                    ) : null}
                    {!stackedActions && secondaryLabel && onSecondary ? (
                      <RiftboundButton
                        treatment="secondary"
                        onClick={onSecondary}
                        disabled={loading}
                        flex="1"
                        minH="44px"
                        borderRadius="full"
                        bg="whiteAlpha.200"
                        borderColor="transparent"
                      >
                        {secondaryLabel}
                      </RiftboundButton>
                    ) : null}
                    <RiftboundButton
                      treatment="primary"
                      colorPalette={confirmTreatment === 'destructive' ? 'red' : undefined}
                      loading={loading}
                      onClick={onConfirm}
                      flex="1"
                      minH="44px"
                      borderRadius="full"
                      fontWeight="semibold"
                    >
                      {confirmLabel}
                    </RiftboundButton>
                  </HStack>
                </VStack>
              ) : (
                <HStack gap={2} justify="flex-end" w="full">
                  {showCancel ? (
                    <RiftboundButton
                      ref={cancelRef}
                      onClick={onCancel ?? onClose}
                      disabled={loading}
                    >
                      {cancelLabel}
                    </RiftboundButton>
                  ) : null}
                  {secondaryLabel && onSecondary ? (
                    <RiftboundButton
                      treatment={secondaryTreatment}
                      onClick={onSecondary}
                      disabled={loading}
                    >
                      {secondaryLabel}
                    </RiftboundButton>
                  ) : null}
                  <RiftboundButton
                    treatment={confirmTreatment === 'primary' ? 'primary' : 'ghost'}
                    colorPalette={confirmTreatment === 'destructive' ? 'red' : undefined}
                    loading={loading}
                    onClick={onConfirm}
                  >
                    {confirmLabel}
                  </RiftboundButton>
                </HStack>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
